import React, { useState } from 'react';
import { Box, Stack, TableCell, TableRow, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { Schema } from '../../../../../amplify/data/resource';
import { SelectionSet } from 'aws-amplify/api';
import ContributionsDialog from '../ContributionsList/ContributionsDialog';
import SendButton from '../../../../components/custom/SendButton';
import { he } from 'date-fns/locale';
import { format } from 'date-fns';
import { extractIdentifier } from '../../../../utils/format/format';

// Компонент для отображения статуса сотрудника
const EmployeeStatus: React.FC<{ contributions: FundContribution[] }> = ({ contributions }) => {
  const getEmployeeStatus = () => {
    // Если нет contributions или все PENDING → показываем прочерк
    if (contributions.length === 0) return null;
    
    const pendingContributions = contributions.filter(c => c.status === 'PENDING');
    if (pendingContributions.length === contributions.length) return null;
    
    const acceptedStatuses = ['ACCEPTED', 'ACCEPTED_WITH_ERROR_AKA'];
    const hasActionRequired = contributions.some(c => c.status === 'ACTION_REQUIRED');
    const acceptedContributions = contributions.filter(c => acceptedStatuses.includes(c.status || ''));
    const processingContributions = contributions.filter(c => c.status === 'PROCESSING');
    
    // Если есть хоть одна ACTION_REQUIRED → שגיאה
    if (hasActionRequired) {
      return 'שגיאה';
    }
    
    // Если все ACCEPTED или ACCEPTED_WITH_ERROR_AKA → רשומה נקלטה בהצלחה
    if (acceptedContributions.length === contributions.length) {
      return 'רשומה נקלטה בהצלחה';
    }
    
    // Если есть хоть один ACCEPTED и все остальные PROCESSING → נקלט חלקי
    if (acceptedContributions.length > 0 && 
        acceptedContributions.length + processingContributions.length === contributions.length) {
      return 'נקלט חלקי';
    }
    
    // Если все PROCESSING → רשומה בתהליך
    if (processingContributions.length === contributions.length) {
      return 'רשומה בתהליך';
    }
    
    return 'רשומה בתהליך';
  };

  const status = getEmployeeStatus();
  
  // Если статус null (PENDING или пустой массив), показываем прочерк
  if (status === null) {
    return (
      <Typography
        fontWeight={'700'}
        fontSize={'14px'}
        fontFamily={'AlmoniDL'}
      >
        -
      </Typography>
    );
  }
  
  const getStatusColor = () => {
    switch (status) {
      case 'רשומה נקלטה בהצלחה':
        return '#94C729';
      case 'נקלט חלקי':
        return '#F6B209';
      case 'שגיאה':
        return '#FF6F78';
      case 'רשומה בתהליך':
        return '#808080';
      default:
        return '#808080';
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
      <Box
        sx={{
          width: '8px',
          height: '8px',
          minWidth: '8px',
          minHeight: '8px',
          borderRadius: '50%',
          backgroundColor: getStatusColor(),
          flexShrink: 0,
        }}
      />
      <Typography
        fontWeight={'700'}
        fontSize={'14px'}
        fontFamily={'AlmoniDL'}
        sx={{ color: '#000000' }}
      >
        {status}
      </Typography>
    </Box>
  );
};

const employeeSelectionSet = [
  'id',
  'firstName',
  'lastName',
  'employeeId',
  'salaryMonth',
  'depositId'
] as const;

const fundContributionSelectionSet = [
  'id',
  'contributionType',
  'contributionAmount',
  'contributionRate',
  'exemptPayments',
  'registryIdentifier',
  'previousRegistryIdentifier',
  'status',
  'error',
  'employeeId',
  'salaryMonth',
] as const;

const depositSelectionSet = [
  'id',
  'totalDepositAmount',
  'fundIdentifier',
  'fundNameAtEmployer',
] as const;

// @ts-ignore
type Employee = SelectionSet<Schema['Employee']['type'], typeof employeeSelectionSet>;

// @ts-ignore
type FundContribution = SelectionSet<Schema['FundContribution']['type'], typeof fundContributionSelectionSet>;

// @ts-ignore
type Deposit = SelectionSet<Schema['Deposit']['type'], typeof depositSelectionSet>;

interface EmployeeProps {
  employee: Employee;
  fundContributions: FundContribution[];
  deposits: Deposit[];
}

const Employee: React.FC<EmployeeProps> = ({ employee, fundContributions, deposits }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
  

  const employeeContributions = fundContributions.filter(
    contribution => contribution.employeeId === employee.id
  );


  const employeeDeposit = deposits.find(deposit => deposit.id === employee.depositId);

  const handleOpenDialog = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: '#F2F4F7',
          borderRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          verticalAlign: 'bottom',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <TableCell
          sx={{
            py: '20px',
            px: '30px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {fullName}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            -
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            -
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {(employeeDeposit as any)?.fundNameAtEmployer || '-'}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {extractIdentifier((employeeDeposit as any)?.fundIdentifier)}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {employee.salaryMonth
              ? format(new Date(employee.salaryMonth), 'LLLL yyyy', {
                  locale: he,
                })
              : ''}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            -
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <EmployeeStatus contributions={employeeContributions} />
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            -
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
            borderTopLeftRadius: '10px',
            borderBottomLeftRadius: '10px',
            boxSizing: 'border-box',
          }}
        >
          <SendButton
            loading={false}
            label=""
            contrast
            onClick={handleOpenDialog}
          />
        </TableCell>
      </TableRow>

      <ContributionsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        contributions={employeeContributions}
        employeeName={fullName}
      />
    </>
  );
};

export default Employee;
